import { useState } from 'react'
import { ChevronDown, ChevronRight, FileText } from 'lucide-react'
import { cn } from '@/lib/utils/cn'
import type { DocumentGroup } from '../utils/grouping'
import type { DocumentRow } from '../api/types'
import { DocumentActionsMenu } from './DocumentActionsMenu'
import { DocumentStatusChip } from './DocumentStatusChip'

export interface DocumentListProps {
  groups: DocumentGroup[]
  selectedId: number | null
  onSelect: (row: DocumentRow) => void
  /** When true, renders the per-row `⋯` menu (admin+regulatory surface). */
  canAct: boolean
  /** Opens the batch uploader at the page level. */
  onRequestBatchUpload: (row: DocumentRow) => void
}

/**
 * Vendor-grouped list of lots. Each group header is click-to-toggle.
 *
 * Groups default to **collapsed** (post-Sprint-04b fix 2026-04-20): the
 * production dataset has ~40+ vendor groups and 2,594 lots; rendering
 * all groups expanded is unusable. Users scan the compact vendor index
 * first, then expand the group they want to drill into.
 *
 * Collapsed state is keyed by `group.key`, so when the parent re-
 * computes groups (after a filter/search change), any new group keys
 * start collapsed. Existing keys keep their expand/collapse state
 * across filter transitions — re-typing a search that still surfaces
 * the same vendor doesn't re-collapse an expanded group.
 */
export function DocumentList({
  groups,
  selectedId,
  onSelect,
  canAct,
  onRequestBatchUpload,
}: DocumentListProps) {
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set())

  function toggleGroup(key: string) {
    setExpandedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  return (
    <div className="flex flex-col gap-3">
      {groups.map((group) => {
        const expanded = expandedKeys.has(group.key)
        return (
          <section key={group.key} className="flex flex-col gap-1.5">
            {group.label ? (
              <button
                type="button"
                onClick={() => toggleGroup(group.key)}
                aria-expanded={expanded}
                className="text-role-section-label flex cursor-pointer items-center gap-2 rounded-sm border border-transparent bg-transparent px-1 py-1 text-left hover:border-border-subtle hover:bg-surface-hover hover:text-primary"
              >
                {expanded ? (
                  <ChevronDown size={12} className="flex-shrink-0" />
                ) : (
                  <ChevronRight size={12} className="flex-shrink-0" />
                )}
                <span>{group.label}</span>
                <span className="rounded-full bg-muted px-2 py-0.5 font-sans text-2xs normal-case text-secondary">
                  {group.items.length}
                </span>
              </button>
            ) : null}
            {expanded || !group.label ? (
              <ul className="flex flex-col rounded-sm border border-border-subtle bg-surface">
                {group.items.map((row) => (
                  <ListRow
                    key={row.lot_id}
                    row={row}
                    selected={selectedId === row.lot_id}
                    onClick={onSelect}
                    canAct={canAct}
                    onRequestBatchUpload={onRequestBatchUpload}
                  />
                ))}
              </ul>
            ) : null}
          </section>
        )
      })}
    </div>
  )
}

function ListRow({
  row,
  selected,
  onClick,
  canAct,
  onRequestBatchUpload,
}: {
  row: DocumentRow
  selected: boolean
  onClick: (row: DocumentRow) => void
  canAct: boolean
  onRequestBatchUpload: (row: DocumentRow) => void
}) {
  return (
    <li
      className={cn(
        'group flex min-h-14 cursor-pointer items-center gap-3 border-b border-border-subtle px-3 py-2 text-sm transition-colors duration-fast last:border-b-0',
        'hover:bg-surface-hover',
        selected && 'bg-accent-subtle',
      )}
      onClick={() => onClick(row)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick(row)
        }
      }}
    >
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xs bg-surface-sunk">
        <FileText size={20} className="text-tertiary" aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span
            className="text-role-data-primary min-w-0 break-words"
            title={row.lot_name}
          >
            {row.lot_name}
          </span>
          <span className="truncate text-sm text-secondary">{row.product_name}</span>
        </div>
        <div className="text-role-metadata flex items-center gap-2">
          <span className="truncate">{row.vendor_name || '(no vendor)'}</span>
          <span className="opacity-40">·</span>
          <span>Exp {row.expiration_date ?? '—'}</span>
          <span className="opacity-40">·</span>
          <span>Mfg {row.manufacture_date ?? '—'}</span>
        </div>
      </div>
      <DocumentStatusChip status={row.status} />
      <DocumentActionsMenu
        row={row}
        canAct={canAct}
        onRequestBatchUpload={onRequestBatchUpload}
      />
    </li>
  )
}
